// src/hooks/useImpersonationFromUrl.js
//
// Day 1 debug affordance for the multi-party PadRekko QC build. A platform
// admin can switch the active org for a session by appending ?as_org={uuid}
// to any URL. The hook reads the param on mount, persists it to localStorage,
// strips it from the URL bar, and syncs it to usePlatformOrgContext —
// which feeds the X-Active-Org header injected by fetchWithAuth.
//
// Special value `?as_org=clear` removes any prior impersonation.
//
// On a fresh load with no param, the hook re-hydrates from localStorage so
// the impersonation persists across page refreshes within the same browser.
//
// v1 will replace this with a proper org-picker dropdown for platform admins.

import { useEffect } from "react";
import {
  usePlatformOrgContext,
  readImpersonation,
  writeImpersonation,
} from "../stores/usePlatformOrgContext";
import { fetchWithAuth } from "../api/fetchWithAuth";

const UUID_RE = /^[0-9a-f-]{36}$/i;

// Fetch the full impersonated org details (id, name, slug,
// is_service_provider) and populate usePlatformOrgContext.activeOrg.
// fetchWithAuth already sends X-Active-Org from the same store, so
// /orgs/active returns the impersonated org's row. Failures leave
// activeOrg null — consumers using useEffectiveOrg() then fall back
// to the user's real org (or fail closed on is_service_provider
// checks during the brief pending window).
async function loadImpersonatedOrgDetails() {
  try {
    const res = await fetchWithAuth("/api/v1/orgs/active");
    if (!res.ok) return;
    const org = await res.json();
    usePlatformOrgContext.getState().setActiveOrgDetails(org);
  } catch (err) {
    console.warn("[impersonation] /orgs/active fetch failed", err);
  }
}

export function useImpersonationFromUrl() {
  const setActiveOrg  = usePlatformOrgContext((s) => s.setActiveOrg);
  const clearActiveOrg = usePlatformOrgContext((s) => s.clearActiveOrg);

  useEffect(() => {
    let asOrg = null;
    try {
      const params = new URLSearchParams(window.location.search);
      asOrg = params.get("as_org");
    } catch {
      // Older browsers / SSR edge — fail silently, hook is dev-only.
    }

    if (asOrg === "clear") {
      // Explicit clear path.
      writeImpersonation(null);
      clearActiveOrg();
      stripAsOrgFromUrl();
      return;
    }

    if (asOrg && UUID_RE.test(asOrg)) {
      writeImpersonation(asOrg);
      // name: null — loadImpersonatedOrgDetails() supplies the real one from
        // /api/v1/orgs/active a moment later, and every consumer guards on
        // `activeOrgName &&`. A hardcoded-list lookup here rendered a UUID
        // prefix for any org outside the demo three.
        setActiveOrg({ id: asOrg, name: null });
      stripAsOrgFromUrl();
      loadImpersonatedOrgDetails();
      return;
    }

    // No URL param — re-hydrate from storage so impersonation survives a
    // refresh. Per tab now, not per browser; readImpersonation adopts a
    // pre-sessionStorage localStorage value once and deletes it.
    const stored = readImpersonation();
    if (stored && UUID_RE.test(stored)) {
      setActiveOrg({ id: stored, name: null });
      loadImpersonatedOrgDetails();
    }
    // Run-once on mount — no deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function stripAsOrgFromUrl() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("as_org")) return;
    url.searchParams.delete("as_org");
    // replaceState so this doesn't add a back-button history entry.
    window.history.replaceState(null, "", url.toString());
  } catch {
    // noop
  }
}
